import { InterviewDetails, InterviewFeedback, FeedbackCategory } from '../types/interviews';

// Define the API response type
interface ApiResponse {
  success: boolean;
  data?: any;
  error?: string;
  reason?: string;
}

// Constants
const API_BASE_URL = process.env.REACT_APP_INTERVIEW_PROXY_API_URL || 'http://localhost:8000';

/**
 * Service for generating interview feedback
 */
class FeedbackService {
  private static instance: FeedbackService;
  
  private constructor() {
    console.log('FeedbackService initialized');
  }

  /**
   * Get the singleton instance of FeedbackService
   */
  public static getInstance(): FeedbackService {
    if (!FeedbackService.instance) {
      FeedbackService.instance = new FeedbackService();
    }
    return FeedbackService.instance;
  }

  /**
   * Generate interview feedback using backend API
   * @param interviewDetails The interview details
   * @param interviewSessionId The interview session ID
   * @returns The interview feedback
   */
  public async generateFeedback(
    interviewDetails: InterviewDetails,
    interviewSessionId: string
  ): Promise<InterviewFeedback> {
    console.log('Generating feedback via backend API for user interview ID:', interviewDetails.userInterviewId);

    try {
      // Get the auth token
      const token = localStorage.getItem('mocktrail-token');
      
      // Ensure we have all needed fields for the interview context
      const completeInterviewDetails = {
        ...interviewDetails,
        // Make sure these fields exist even if they're null
        characterGender: interviewDetails.characterGender || 'RANDOM',
        interviewPrompt: interviewDetails.interviewPrompt || '',
      };
      
      console.log('[FeedbackService] Sending request for interview:', completeInterviewDetails);
      console.log('[FeedbackService] Interview session ID:', interviewSessionId);
      
      // Call our backend API directly with fetch
      const response = await fetch(`${API_BASE_URL}/api/interview-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          interviewSessionId: interviewSessionId,
          accessToken: token
          // No need to send transcriptions - backend will handle it
        })
      });
      
      const responseData: ApiResponse = await response.json();
      console.log('[FeedbackService] Response received from backend API:', responseData);
      
      if (!responseData || !responseData.success) {
        console.error('[FeedbackService] Error from backend:', responseData?.error || 'Unknown error');
        
        // Return feedback with only the interview details as backend didn't return any feedback
        return {
          interviewDetails: interviewDetails,
        };
        // Return mock feedback for other errors
        // return this.generateLowQualityFeedback(interviewDetails, responseData?.error || 'Unknown error');
      }
      
      const feedbackData = responseData.data;
      
      // Map the feedback data to our InterviewFeedback interface
      const feedback: InterviewFeedback = this.mapFeedbackResponse(feedbackData, interviewDetails);
      
      console.log('Feedback generated successfully for user interview ID:', interviewDetails.userInterviewId, feedback);
      return feedback;
    } catch (error) {
      console.error('Error generating feedback with backend API:', error);
      
      // Return mock feedback in case of error
      return this.generateLowQualityFeedback(interviewDetails, 'Unknown error');
    }
  }

  /**
   * Generate feedback for implementor interviews using the separate endpoint
   * @param interviewDetails The interview details
   * @param interviewSessionId The interview session ID
   * @returns The interview feedback
   */
  public async generateImplementorFeedback(
    interviewDetails: InterviewDetails,
    interviewSessionId: string
  ): Promise<InterviewFeedback> {
    console.log('[IMPLEMENTOR] Generating feedback via implementor endpoint for session:', interviewSessionId);

    try {
      // Get the auth token
      const token = localStorage.getItem('mocktrail-token');

      console.log('[FeedbackService] [IMPLEMENTOR] Sending request for session:', interviewSessionId);

      // Call the implementor-specific backend endpoint
      const response = await fetch(`${API_BASE_URL}/api/interview-feedback-implementor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          interviewSessionId: interviewSessionId,
          accessToken: token
        })
      });

      const responseData: ApiResponse = await response.json();
      console.log('[FeedbackService] [IMPLEMENTOR] Response received:', responseData);

      if (!responseData || !responseData.success) {
        console.error('[FeedbackService] [IMPLEMENTOR] Error from backend:', responseData?.error || 'Unknown error');

        // Return feedback with only the interview details
        return {
          interviewDetails: interviewDetails,
        };
      }

      const feedbackData = responseData.data;

      // Map the feedback data to our InterviewFeedback interface
      const feedback: InterviewFeedback = this.mapFeedbackResponse(feedbackData, interviewDetails);

      console.log('[IMPLEMENTOR] Feedback generated successfully:', feedback);
      return feedback;
    } catch (error) {
      console.error('[IMPLEMENTOR] Error generating feedback:', error);

      // Return mock feedback in case of error
      return this.generateLowQualityFeedback(interviewDetails, 'Implementor feedback generation failed');
    }
  }

  /**
   * Map the feedback response from the backend API to our InterviewFeedback interface
   */
  private mapFeedbackResponse(
    feedbackData: any, 
    interviewDetails: InterviewDetails
  ): InterviewFeedback {
    console.log('Mapping feedback response to InterviewFeedback interface for user interview ID:', interviewDetails.userInterviewId, interviewDetails.title, interviewDetails.company, interviewDetails.jobRole, interviewDetails.duration, interviewDetails.minutesUsed);
    
    // Validate and ensure all required fields are present
    if (!feedbackData.categories || !Array.isArray(feedbackData.categories)) {
      feedbackData.categories = [];
    }
    
    if (!feedbackData.skills || !Array.isArray(feedbackData.skills)) {
      feedbackData.skills = [];
    }
    
    if (!feedbackData.strengths || !Array.isArray(feedbackData.strengths)) {
      feedbackData.strengths = [];
    }
    
    if (!feedbackData.improvements || !Array.isArray(feedbackData.improvements)) {
      feedbackData.improvements = [];
    }
    
    if (!feedbackData.impression || typeof feedbackData.impression !== 'object') {
      feedbackData.impression = { rating: 3, comment: "No impression provided." };
    } else if (!feedbackData.impression.comment) {
      // If impression exists but has no comment, add a default comment
      feedbackData.impression.comment = "No impression notes provided.";
    }
    
    // Smart icon and color mapping based on category name keywords
    const mapCategoryIconAndColor = (categoryName: string) => {
      const name = categoryName.toLowerCase();
      
      // Technical and Development related
      if (name.includes('technical') || name.includes('code') || name.includes('programming') || 
          name.includes('development') || name.includes('software') || name.includes('algorithm') ||
          name.includes('coding') || name.includes('implementation') || name.includes('debugging') ||
          name.includes('refactoring') || name.includes('optimization')) {
        return { icon: 'Code', color: 'bg-blue-500' };
      }
      
      // Problem Solving and Logic
      if (name.includes('problem') || name.includes('solving') || name.includes('logic') || 
          name.includes('analytical') || name.includes('critical') || name.includes('reasoning') ||
          name.includes('thinking') || name.includes('approach') || name.includes('methodology') ||
          name.includes('troubleshooting') || name.includes('decision')) {
        return { icon: 'BrainCircuit', color: 'bg-purple-500' };
      }
      
      // Communication and Presentation
      if (name.includes('communication') || name.includes('presentation') || name.includes('verbal') || 
          name.includes('explanation') || name.includes('articulation') || name.includes('clarity') ||
          name.includes('speaking') || name.includes('listening') || name.includes('interpersonal') ||
          name.includes('writing') || name.includes('documentation') || name.includes('storytelling')) {
        return { icon: 'Volume2', color: 'bg-green-500' };
      }
      
      // Time Management and Organization
      if (name.includes('time') || name.includes('management') || name.includes('organization') || 
          name.includes('efficiency') || name.includes('planning') || name.includes('deadline') ||
          name.includes('scheduling') || name.includes('prioritization') || name.includes('workflow') ||
          name.includes('productivity') || name.includes('execution')) {
        return { icon: 'Clock', color: 'bg-orange-500' };
      }
      
      // System Design and Architecture
      if (name.includes('system') || name.includes('design') || name.includes('architecture') || 
          name.includes('scalability') || name.includes('infrastructure') || name.includes('distributed') ||
          name.includes('microservices') || name.includes('database') || name.includes('performance') ||
          name.includes('reliability') || name.includes('availability') || name.includes('consistency')) {
        return { icon: 'Layers', color: 'bg-indigo-500' };
      }
      
      // Data and Analytics
      if (name.includes('data') || name.includes('analytics') || name.includes('analysis') || 
          name.includes('statistics') || name.includes('machine learning') || name.includes('ai') ||
          name.includes('modeling') || name.includes('visualization') || name.includes('insights') ||
          name.includes('metrics') || name.includes('reporting') || name.includes('intelligence')) {
        return { icon: 'BarChart3', color: 'bg-cyan-500' };
      }
      
      // Business and Strategy
      if (name.includes('business') || name.includes('strategy') || name.includes('stakeholder') || 
          name.includes('product') || name.includes('vision') || name.includes('roadmap') ||
          name.includes('market') || name.includes('commercial') || name.includes('revenue') ||
          name.includes('growth') || name.includes('planning') || name.includes('objectives')) {
        return { icon: 'Briefcase', color: 'bg-emerald-500' };
      }
      
      // Leadership and Teamwork
      if (name.includes('leadership') || name.includes('team') || name.includes('collaboration') || 
          name.includes('mentor') || name.includes('coaching') || name.includes('delegation') ||
          name.includes('coordination') || name.includes('facilitation') || name.includes('influence') ||
          name.includes('motivation') || name.includes('conflict') || name.includes('consensus')) {
        return { icon: 'Users', color: 'bg-pink-500' };
      }
      
      // Creativity and Innovation
      if (name.includes('creative') || name.includes('innovation') || name.includes('ideation') || 
          name.includes('user experience') || name.includes('ux') || name.includes('ui') ||
          name.includes('visual') || name.includes('aesthetic') || name.includes('artistic') ||
          name.includes('originality') || name.includes('imagination') || name.includes('invention')) {
        return { icon: 'Palette', color: 'bg-rose-500' };
      }
      
      // Sales and Customer related
      if (name.includes('sales') || name.includes('customer') || name.includes('persuasion') || 
          name.includes('negotiation') || name.includes('relationship') || name.includes('client') ||
          name.includes('revenue') || name.includes('conversion') || name.includes('acquisition') ||
          name.includes('retention') || name.includes('service') || name.includes('satisfaction')) {
        return { icon: 'TrendingUp', color: 'bg-yellow-500' };
      }
      
      // Security and Compliance
      if (name.includes('security') || name.includes('compliance') || name.includes('risk') || 
          name.includes('governance') || name.includes('audit') || name.includes('privacy') ||
          name.includes('protection') || name.includes('vulnerability') || name.includes('threat') ||
          name.includes('encryption') || name.includes('authentication') || name.includes('authorization')) {
        return { icon: 'Shield', color: 'bg-red-500' };
      }
      
      // Quality and Testing
      if (name.includes('quality') || name.includes('testing') || name.includes('qa') || 
          name.includes('debugging') || name.includes('validation') || name.includes('verification') ||
          name.includes('review') || name.includes('inspection') || name.includes('assurance') ||
          name.includes('standards') || name.includes('best practices') || name.includes('excellence')) {
        return { icon: 'CheckCircle', color: 'bg-teal-500' };
      }
      
      // Project Management
      if (name.includes('project') || name.includes('agile') || name.includes('scrum') || 
          name.includes('kanban') || name.includes('sprint') || name.includes('milestone') ||
          name.includes('delivery') || name.includes('coordination') || name.includes('tracking') ||
          name.includes('resource') || name.includes('scope') || name.includes('timeline')) {
        return { icon: 'Calendar', color: 'bg-violet-500' };
      }
      
      // Finance and Operations
      if (name.includes('finance') || name.includes('budget') || name.includes('cost') || 
          name.includes('operations') || name.includes('process') || name.includes('efficiency') ||
          name.includes('optimization') || name.includes('logistics') || name.includes('supply') ||
          name.includes('procurement') || name.includes('vendor') || name.includes('contract')) {
        return { icon: 'DollarSign', color: 'bg-green-600' };
      }
      
      // Research and Learning
      if (name.includes('research') || name.includes('learning') || name.includes('knowledge') || 
          name.includes('investigation') || name.includes('discovery') || name.includes('exploration') ||
          name.includes('study') || name.includes('analysis') || name.includes('academic') ||
          name.includes('scientific') || name.includes('methodology') || name.includes('evidence')) {
        return { icon: 'BookOpen', color: 'bg-indigo-600' };
      }
      
      // Digital Marketing
      if (name.includes('marketing') || name.includes('digital') || name.includes('campaign') || 
          name.includes('brand') || name.includes('content') || name.includes('social media') ||
          name.includes('seo') || name.includes('advertising') || name.includes('promotion') ||
          name.includes('engagement') || name.includes('reach') || name.includes('awareness')) {
        return { icon: 'Megaphone', color: 'bg-orange-600' };
      }
      
      // Healthcare and Medical
      if (name.includes('medical') || name.includes('healthcare') || name.includes('clinical') || 
          name.includes('patient') || name.includes('diagnosis') || name.includes('treatment') ||
          name.includes('therapy') || name.includes('pharmaceutical') || name.includes('nursing') ||
          name.includes('surgical') || name.includes('wellness') || name.includes('health')) {
        return { icon: 'Heart', color: 'bg-red-600' };
      }
      
      // Education and Training
      if (name.includes('education') || name.includes('teaching') || name.includes('training') || 
          name.includes('instruction') || name.includes('curriculum') || name.includes('pedagogy') ||
          name.includes('learning') || name.includes('development') || name.includes('mentoring') ||
          name.includes('coaching') || name.includes('facilitation') || name.includes('workshop')) {
        return { icon: 'GraduationCap', color: 'bg-blue-600' };
      }
      
      // Legal and Compliance
      if (name.includes('legal') || name.includes('law') || name.includes('regulation') || 
          name.includes('compliance') || name.includes('contract') || name.includes('litigation') ||
          name.includes('intellectual property') || name.includes('patent') || name.includes('trademark') ||
          name.includes('policy') || name.includes('ethics') || name.includes('governance')) {
        return { icon: 'Scale', color: 'bg-gray-600' };
      }
      
      // Default fallback
      return { icon: 'Star', color: 'bg-purple-500' };
    };
    
    // If no categories or less than 4 categories, we'll use what's provided
    // The AI should now provide exactly 4 relevant categories
    
    // Ensure each category has all required fields with smart icon/color mapping
    feedbackData.categories = feedbackData.categories.map((category: any) => {
      const mappedIconColor = mapCategoryIconAndColor(category.name || 'Unknown Category');
      
      return {
        name: category.name || 'Unknown Category',
        score: typeof category.score === 'number' ? category.score : 50,
        feedback: category.feedback || 'No feedback provided for this category.',
        icon: mappedIconColor.icon,
        color: mappedIconColor.color
      };
    });
    
    // Ensure skills have all required fields
    feedbackData.skills = feedbackData.skills.map((skill: any) => {
      return {
        name: skill.name || 'Unknown Skill',
        score: typeof skill.score === 'number' ? Math.min(Math.max(skill.score, 1), 5) : 3, // Ensure score is between 1-5
        feedback: skill.feedback || 'No specific feedback provided for this skill.'
      };
    });
    
    // If no skills provided, then it means the interview was not conducted properly
    if (feedbackData.skills.length === 0) {
      console.log('No skills provided, this means the interview was not conducted properly');
    }
    // Ensure strengths have all required fields
    feedbackData.strengths = feedbackData.strengths.map((strength: any) => {
      return {
        text: strength.text || strength.title || 'Unnamed Strength',
        reason: strength.reason || 'No specific reason provided.'
      };
    });
    
    // If no strengths provided, add a default one
    if (feedbackData.strengths.length === 0) {
      feedbackData.strengths.push({
        text: 'Interview Participation',
        reason: 'You participated in the interview process, which is the first step to improvement.'
      });
    }
    
    // Ensure improvements have all required fields
    feedbackData.improvements = feedbackData.improvements.map((improvement: any) => {
      return {
        text: improvement.text || improvement.title || 'Unnamed Area for Improvement',
        reason: improvement.reason || 'No specific reason provided.'
      };
    });
    
    // If no improvements provided, add default ones
    if (feedbackData.improvements.length === 0) {
      feedbackData.improvements.push({
        text: 'More Detailed Responses',
        reason: 'Consider providing more specific examples and detailed explanations in your answers.'
      });
      feedbackData.improvements.push({
        text: 'Technical Depth',
        reason: 'Demonstrate deeper technical knowledge by explaining concepts in more detail.'
      });
    }
    
    // Map feedback categories - now they already have icons and colors assigned
    const feedbackCategories: FeedbackCategory[] = feedbackData.categories.map((category: any) => ({
        name: category.name,
        score: category.score,
        feedback: category.feedback,
      icon: category.icon,
      color: category.color
    }));

    // Return the mapped feedback with all required fields
    return {
      interviewDetails: {
        id: interviewDetails.id,
        userInterviewId: interviewDetails.userInterviewId,
        title: interviewDetails.title,
        company: interviewDetails.company,
        jobRole: interviewDetails.jobRole,
        duration: interviewDetails.duration,
        minutesUsed: interviewDetails.minutesUsed,
        scheduledAt: interviewDetails.scheduledAt,
        status: interviewDetails.status,
        selectedResume: interviewDetails.selectedResume,
        language: interviewDetails.language,
        description: interviewDetails.description,
        createdAt: interviewDetails.createdAt,
        updatedAt: interviewDetails.updatedAt,
        newInterview: interviewDetails.newInterview
      },
      overallScore: typeof feedbackData.overallScore === 'number' ? feedbackData.overallScore : 50,
      categories: feedbackCategories,
      skills: feedbackData.skills || [],
      strengths: feedbackData.strengths || [],
      improvements: feedbackData.improvements || [],
      impression: feedbackData.impression || { 
        rating: 3, 
        comment: "The candidate showed adequate preparation and skill level." 
      },
      summary: feedbackData.summary || "No summary provided."
    };
  }

  /**
   * Generate simplified feedback for low-quality interviews
   */
  private generateLowQualityFeedback(interviewDetails: InterviewDetails, reason: string): InterviewFeedback {
    console.log(`[FeedbackService] Generating low-quality feedback due to: ${reason}`);
    
    const lowScore = Math.floor(Math.random() * 10) + 10; // Random score between 10-20
    
    return {
      interviewDetails,
      overallScore: lowScore,
      categories: [
        {
          name: 'Interview Engagement',
          score: lowScore,
          icon: 'MessageCircle',
          color: 'bg-blue-500',
          feedback: 'Insufficient participation to assess engagement level. A thorough interview requires substantive responses to questions.'
        },
        {
          name: 'Response Quality',
          score: lowScore,
          icon: 'BrainCircuit',
          color: 'bg-purple-500',
          feedback: 'Unable to evaluate response quality due to minimal engagement with interview questions.'
        },
        {
          name: 'Professional Communication',
          score: lowScore,
          icon: 'Volume2',
          color: 'bg-green-500',
          feedback: 'Communication was minimal and insufficient for a professional interview setting.'
        },
        {
          name: 'Interview Preparation',
          score: lowScore,
          icon: 'BookOpen',
          color: 'bg-orange-500',
          feedback: 'Interview participation was too brief to demonstrate preparation and readiness.'
        }
      ],
      skills: [
        {
          name: 'Interview Engagement',
          score: 1,
          feedback: 'Minimal engagement with interview questions. Practice providing detailed responses to showcase your knowledge.'
        },
        {
          name: 'Communication',
          score: 1,
          feedback: 'Very limited communication during the interview. Work on expressing your thoughts more clearly.'
        }
      ],
      strengths: [
        {
          text: 'Showed up for the interview',
          reason: 'You participated in the interview process, which is the first step toward improvement.'
        }
      ],
      improvements: [
        {
          text: 'Engage fully with interview questions',
          reason: 'Provide detailed, thoughtful responses to interview questions. One-word or very brief answers don\'t demonstrate your knowledge or skills.'
        },
        {
          text: 'Demonstrate expertise in your field',
          reason: 'Technical interviews require you to explain your thinking, approach problems methodically, and showcase your expertise.'
        },
        {
          text: 'Practice professional communication',
          reason: 'Professional interviews require clear, comprehensive answers that demonstrate both your knowledge and communication skills.'
        }
      ],
      impression: {
        rating: 1,
        comment: `This interview contained minimal participation (${reason}). In a real interview setting, this level of engagement would not allow the interviewer to assess your qualifications effectively.`
      },
      summary: `This interview didn't contain enough substantive content to provide an accurate assessment. ${reason}. For effective interview practice and meaningful feedback, please engage fully with interview questions by providing detailed answers that showcase your knowledge and skills.`
    };
  }

}

export default FeedbackService; 